import { useMemo, useState } from 'react';
import {
  BadgeQuestionMark,
  ChevronsUpDown,
  LogOut,
  Moon,
  Sun,
  TriangleAlert,
  RefreshCw,
  Users,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@zawarski/eti-ui';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import ThemeDetect from '@/hooks/useThemeQuery';
import { useSidebar } from '@/components/ui/sidebar';
import { useGetAllActiveIncidentsQuery } from '@/services/incidentsApi';
import { useGetPalmettoGroupsQuery } from '@/services/palmettoGroupApi';
import { useAppSelector } from '@/store/hooks';
import {
  selectIsStateUser,
  selectActiveCountyId,
  selectIncidentGatheringId,
  selectIncidentViewingId,
  selectCanSetGathering,
} from '@/store/auth';
import { toTitleCase, getGroupDisplayName } from '@/lib/utils';
import { ChangeSettingModal, type SettingType } from '@/components/modals';

interface NavUserProps {
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export function NavUser({ user }: NavUserProps) {
  const { isMobile, setOpenMobile, setOpen } = useSidebar();
  const { isDarkMode, toggleTheme } = ThemeDetect();
  const activeGroup = useAppSelector(selectActiveCountyId);
  const isStateUser = useAppSelector(selectIsStateUser);
  const incidentViewingId = useAppSelector(selectIncidentViewingId);
  const incidentGatheringId = useAppSelector(selectIncidentGatheringId);
  const canSetGathering = useAppSelector(selectCanSetGathering);

  const [editingSetting, setEditingSetting] = useState<SettingType | null>(null);

  // Fetch incidents and groups for names
  const { data: incidentsData } = useGetAllActiveIncidentsQuery();
  const { data: palmettoGroups = [] } = useGetPalmettoGroupsQuery();

  // Get display names
  const dataViewingIncidentName = useMemo(() => {
    if (!incidentViewingId || !incidentsData?.dataset) return null;
    const incident = incidentsData.dataset.find(
      (inc) => inc.pvIncidentID === Number(incidentViewingId)
    );
    return incident?.pvIncidentName ?? null;
  }, [incidentViewingId, incidentsData]);

  const dataGatheringIncidentName = useMemo(() => {
    if (!incidentGatheringId || !incidentsData?.dataset) return null;
    const incident = incidentsData.dataset.find(
      (inc) => inc.pvIncidentID === Number(incidentGatheringId)
    );
    return incident?.pvIncidentName ?? null;
  }, [incidentGatheringId, incidentsData]);

  const activeGroupName = useMemo(() => {
    if (!activeGroup) return null;
    const group = palmettoGroups.find((g) => g.pvGroupID === activeGroup);
    return getGroupDisplayName(group) || null;
  }, [activeGroup, palmettoGroups]);

  const fullName = `${user.firstName} ${user.lastName}`;
  const avatarInitials = user.firstName.charAt(0) + user.lastName.charAt(0);

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
    window.dispatchEvent(new CustomEvent('open-contact-support'));
  };

  const handleSettingClick = (type: SettingType) => {
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
    setEditingSetting(type);
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger className='w-full'>
              <SidebarMenuButton
                size='lg'
                className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground w-full'>
                <Avatar className='h-8 w-8 rounded-full'>
                  <AvatarFallback className='bg-primary text-white'>
                    {avatarInitials}
                  </AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-medium'>{fullName}</span>
                  <span className='truncate text-xs'>{user.email}</span>
                </div>
                <ChevronsUpDown className='ml-auto size-4' />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
              side={isMobile ? 'bottom' : 'right'}
              align='end'
              sideOffset={4}>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className='flex cursor-pointer items-center gap-2 py-2 font-normal'
                  onClick={() => handleSettingClick('viewing')}>
                  <TriangleAlert className='size-4 shrink-0' />
                  <div className='grid text-left text-sm leading-tight'>
                    <span className='font-medium'>Data Viewing Incident</span>
                    <span className='text-muted-foreground text-xs'>
                      {toTitleCase(dataViewingIncidentName) || 'Not set'}
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={`flex items-center gap-2 py-2 font-normal ${canSetGathering ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                  onClick={canSetGathering ? () => handleSettingClick('gathering') : undefined}>
                  <RefreshCw className='size-4 shrink-0' />
                  <div className='grid text-left text-sm leading-tight'>
                    <span className='font-medium'>Data Gathering Incident</span>
                    <span className='text-muted-foreground text-xs'>
                      {toTitleCase(dataGatheringIncidentName) || 'Not set'}
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={`flex items-center gap-2 py-2 font-normal ${isStateUser ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                  onClick={isStateUser ? () => handleSettingClick('group') : undefined}>
                  <Users className='size-4 shrink-0' />
                  <div className='grid text-left text-sm leading-tight'>
                    <span className='font-medium'>
                      {activeGroupName === 'SCEMD'
                        ? activeGroupName
                        : toTitleCase(activeGroupName) || 'No Group'}
                    </span>
                    <span className='text-muted-foreground text-xs'>County</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className='bg-muted-foreground/30!' />
              <DropdownMenuGroup>
                <DropdownMenuItem className='cursor-pointer' onClick={handleContactSupport}>
                  <BadgeQuestionMark />
                  Contact Support
                </DropdownMenuItem>
                <DropdownMenuItem className='cursor-pointer' onClick={toggleTheme}>
                  {isDarkMode ? <Sun /> : <Moon />}
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className='bg-muted-foreground/30!' />
              <DropdownMenuItem className='cursor-pointer' onClick={handleLogout}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      {editingSetting && (
        <ChangeSettingModal
          open={!!editingSetting}
          onOpenChange={(open) => {
            if (!open) setEditingSetting(null);
          }}
          settingType={editingSetting}
        />
      )}
    </>
  );
}
